// Unified shell context — composed from all sub-app registries.
//
// Why pages must be here (not just in each sub-app context):
//   The smbls router calls `matchRoute(pathname, element.routes)` where
//   `element.routes` is populated from `context.pages` during preparePages().
//   `element` is the ROOT element created by `create(shellApp, context)`.
//   When dispatchUrl() mounts a sub-app via shell.Slot.set(appDef), that sub-app
//   shares the shell's context — its `extends: 'Page'` triggers the router on
//   the root context's pages map. So ALL routes from ALL sub-apps must live here.
//
// Merge precedence (last writer wins for duplicate paths):
//   1. canvas      — /dashboard, /studio/*, /:owner/:projectKey catchall
//   2. marketplace — /marketplace
//   3. preview     — /preview
//   4. cms         — /cms
//   5. workspace-project — / and all workspace routes (highest priority, wins conflicts)
//
// Auth pages (login, signup, etc.) are included in workspace-project's pages
// via identity-index.js, so they don't need a separate import here.
//
// Components/functions/methods: workspace-project is the primary sub-app.
// Its context is the most complete registry; canvas and others supplement via
// their own sharedLibraries. The shell inherits shared/ via sharedLibraries
// (AppNavbar, AppShell, etc.) — workspace-project adds intranet-specific pieces.
//
// SDK wiring: workspaceApiUrl + workspaceProjectTokenProvider are exported
// so bootShell's Pattern B (extended) can auto-instantiate the SDK even
// for multi-app shells. See shared/functions/bootShell.js.

import sharedLibraries from './sharedLibraries.js'
import shellState from './state.js'
import app from './app.js'

// Sub-app state schemas — when the smbls auto-router mounts a sub-app's
// page directly into Slot (no wrapper instantiation), the sub-app's
// state.js declarations need to be present on the shell's root state
// so `root.update({ peopleAll, teams, ... })` calls from the shell-level
// loadPrivateData (or any sub-app page onCreate) actually land — smbls
// 3.14 silently drops keys not in the schema.
import wpState from '../workspace-project/state.js'

// Sub-app pages — cms/inspect/gazer packages aren't on disk in this branch.
import wpPages from '../workspace-project/pages/index.js'
import canvasPages from '../canvas/pages/index.js'
import marketplacePages from '../marketplace/pages/index.js'
import previewPages from '../preview/pages/index.js'

// Sub-app registries (workspace-project is the primary sub-app)
import * as wpComponents from '../workspace-project/components/index.js'
import * as wpFunctions from '../workspace-project/functions/index.js'
import * as wpMethods from '../workspace-project/methods/index.js'

// Canvas + marketplace + preview registries — components, functions, and
// methods must all be merged so pages from every sub-app can resolve
// their own DOMQL components (extends:), call registered functions
// (el.call('resultsCountLabel', ...)), and invoke methods when the smbls
// router mounts them directly into Slot from the unified pages map.
import * as canvasComponents from '../canvas/components/index.js'
import * as canvasFunctions from '../canvas/functions/index.js'
import * as canvasMethods from '../canvas/methods/index.js'
import * as marketplaceComponents from '../marketplace/components/index.js'
import * as marketplaceFunctions from '../marketplace/functions/index.js'
import * as previewComponents from '../preview/components/index.js'

// Config: Supabase fetch adapter + smbls flags (useReset, useFontImport, etc.)
import config from '../workspace-project/config.js'

// workspace-project ships its own designSystem (icons, colors, etc.).
// Without this import the shell sprite only carries brand's icons —
// every workspace-project icon (image, attachment, more, document,
// people, …) would be missing on routes that workspace-project mounts.
import wpDesignSystem from '../workspace-project/designSystem/index.js'

// SDK contracts — same as workspace-project standalone
import envs from '../workspace-project/envs.js'
import { apiUrl as channelApiUrl } from '@symbo.ls/channels'
import { workspaceTokenProvider as workspaceProjectTokenProvider } from '../workspace-project/functions/workspaceTokenProvider.js'

// Compose the full pages map.
// Order: canvas → marketplace → preview → workspace-project.
// workspace-project wins on conflicting routes (e.g. / resolves to wp main,
// not canvas's rootRedirect). Canvas auth routes (signin/signup) are
// overridden by wp identity pages so the intranet auth flow is consistent.
const pages = {
  ...canvasPages,
  ...marketplacePages,
  ...previewPages,
  ...wpPages
}

// Merge sub-app state schema into the shell's. Shell keys win on
// conflicting schema names (the shell's `org`, `orgs`, `workspace`, …
// are JWT-derived; wp's older copies were org-switcher-internal and are
// superseded). Order: wp first → shell overrides.
const state = { ...wpState, ...shellState }

export default {
  // smbls config flags + Supabase fetch adapter
  ...config,

  app,
  state,
  sharedLibraries,

  // Unified route map — required for smbls router to match any path
  pages,

  // Component / function / method registries — merged from EVERY sub-app
  // because the shell's smbls router mounts each sub-app's pages directly
  // into Slot from the unified `pages` map. Each page references its own
  // sub-app's components by name (e.g. canvas's `WorkspaceRowButton`,
  // workspace-project's `IntranetAppHeader`); without this merge the
  // first-loaded sub-app's pages render correctly while every other
  // sub-app's pages render as bare chrome.
  // Order: canvas → marketplace → preview → workspace-project so
  // workspace-project's overrides win on key conflicts (matches the
  // pages-map precedence above).
  components: {
    ...canvasComponents,
    ...marketplaceComponents,
    ...previewComponents,
    ...wpComponents
  },
  // Order: canvas → marketplace → workspace-project so wp overrides win on
  // key conflicts (same precedence as components and pages above).
  functions: {
    ...canvasFunctions,
    ...marketplaceFunctions,
    ...wpFunctions
  },
  methods: { ...canvasMethods, ...wpMethods },

  // workspace-project's designSystem (icons, colors, fonts, spacing,
  // …). Must live under the `designSystem` key — smbls's
  // mergeSharedLibraries calls `deepDefaults(context.designSystem,
  // sharedLib.designSystem)`, so brand fills gaps under this exact
  // path. Spreading at top level would route brand's icons into
  // designSystem.icons while ours sit on context.icons (orphaned, not
  // in the sprite). Target wins on conflicts: workspace-project icons
  // override brand on shared keys; brand fills the rest.
  designSystem: wpDesignSystem,

  // SDK contracts — consumed by bootShell (Pattern B extended) and
  // @symbo.ls/sdk WorkspaceDataService init
  workspaceApiUrl: envs?.workspaceApiUrl || envs?.symbolsApiUrl || channelApiUrl(),
  workspaceProjectTokenProvider
}
